"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContractDocumentBadge = ContractDocumentBadge;
exports.ContractRowContent = ContractRowContent;
/**
 * Wspólna zawartość wiersza rejestru kontraktów.
 *
 * Układ zatwierdzony przez właściciela na makiecie `tmp/makieta-lista-kontraktow-v3.html`,
 * zakładka „Wariant A — jak w Zadaniach". Lista straciła wszystkie kolumny poza jedną
 * (Projekt, Oznaczenie, Rozpoczęcie, Zakończenie, Gwarancja oraz kolumnę znaczników) na rzecz
 * jednego bloku typograficznego.
 *
 * Umowa ENVI i umowa wykonawcy różnią się WYŁĄCZNIE kształtem wiersza — bez kolorowych
 * plakietek roli i bez ikon. Wzorzec przeniesiony z widoku zadań (TasksGlobal.tsx,
 * makeOurContractTitleHeader / makeOtherContractTitleHeader), żeby oba widoki mówiły tym samym
 * językiem:
 *   - umowa ENVI prowadzi identyfikatorem `OurId | alias`, a nazwa kontraktu jest tytułem;
 *   - umowa wykonawcy odwraca hierarchię: prowadzi „hero" `alias · wykonawca` (kotwica
 *     pamięciowa), pod nim identyfikator `typ · numer ➔ NASZA.UMOWA`, a nazwa kontraktu
 *     schodzi na drugi plan.
 * Strzałka do naszej umowy jest tu jedynym nośnikiem relacji nadzoru — dokładnie jak
 * w zadaniach. Brak powiązania NIE jest chowany: wychodzi jako ostrzegawczy tekst w miejscu
 * oznaczenia.
 *
 * Pozostałe decyzje właściciela wbudowane w ten plik:
 *  - umowa ENVI pokazuje Zamawiającego, koordynatora i administratora;
 *  - umowa wykonawcy pokazuje wykonawcę, Zamawiającego i Inżyniera;
 *  - zakresy przy obu rodzajach, na dolnym pasku;
 *  - plakietka integracji z FIDmanem na tym samym pasku, wyrównana do prawej;
 *  - wartość kontraktu NIE wchodzi do wiersza (lista bywa oglądana przy ludziach);
 *  - terminy nieobowiązkowe pojawiają się tylko wtedy, gdy są wpisane.
 */
const react_1 = __importDefault(require("react"));
const react_bootstrap_1 = require("react-bootstrap");
const MainSetupReact_1 = __importDefault(require("../../React/MainSetupReact"));
const CommonComponents_1 = require("../../View/Resultsets/CommonComponents");
const LetterRowContent_1 = require("../../Letters/LettersList/LetterRowContent");
/** Paleta z zatwierdzonej makiety — ta sama co w rejestrze pism (LetterRowContent.tsx). */
const C = {
    ink: "#16211c",
    inkSoft: "#3c4c44",
    muted: "#6b7c73",
    faint: "#94a49b",
    accent: "#166243",
    warn: "#8f5406",
    demoted: "#495057",
    heroSep: "#c7ccd1",
    rangeBg: "#f1f4f2",
    rangeLine: "#e6ece9",
};
const S = {
    line1: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        gap: "8px",
        fontSize: ".8rem",
    },
    /** Identyfikator umowy — drobny, wersalikowy, na drugim planie wobec „hero". */
    cid: {
        fontSize: ".72rem",
        fontWeight: 700,
        color: C.muted,
        textTransform: "uppercase",
        letterSpacing: ".05em",
        fontVariantNumeric: "tabular-nums",
    },
    arrow: { color: C.accent, fontWeight: 700 },
    /** Oznaczenie naszej umowy w osobnym elemencie — nie tylko dla stylu: jako samodzielny
     *  węzeł daje się zaznaczyć i skopiować bez reszty identyfikatora. */
    related: { color: C.inkSoft },
    noRelation: { color: C.warn },
    hero: { fontSize: "1.12rem", fontWeight: 700, color: C.ink, lineHeight: 1.2 },
    heroSep: { color: C.heroSep, fontWeight: 400 },
    nameDemoted: { fontSize: ".92rem", fontWeight: 400, color: C.demoted },
    proj: {
        color: C.muted,
        fontSize: ".76rem",
        fontVariantNumeric: "tabular-nums",
        marginLeft: "auto",
    },
    under: { color: C.muted, fontSize: ".76rem" },
    underStrong: { color: C.inkSoft, fontWeight: 600 },
    bottom: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        marginTop: "10px",
    },
    ranges: { display: "flex", flexWrap: "wrap", gap: "5px" },
    range: {
        display: "inline-block",
        borderRadius: "999px",
        padding: "1px 9px",
        fontSize: ".72rem",
        backgroundColor: C.rangeBg,
        color: C.inkSoft,
        border: `1px solid ${C.rangeLine}`,
    },
    sideBox: { borderLeft: `1px solid ${C.rangeLine}`, paddingLeft: "14px" },
    sideHead: {
        fontSize: ".66rem",
        textTransform: "uppercase",
        letterSpacing: ".08em",
        color: C.faint,
        fontWeight: 700,
        marginBottom: "3px",
    },
    sideRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: "10px",
        fontSize: ".78rem",
        padding: "1px 0",
    },
    sideKey: {
        color: C.faint,
        textTransform: "uppercase",
        letterSpacing: ".06em",
        fontSize: ".66rem",
        fontWeight: 700,
    },
    sideValue: { color: C.inkSoft, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    sideValueOverdue: {
        color: C.warn,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    },
};
function isOurContract(contract) {
    return !!contract.ourId;
}
/** Skrócone nazwy podmiotów, oddzielone kropką — tak samo jak w rejestrze pism. */
function entitiesLabel(entities) {
    if (!entities?.length)
        return "";
    return entities
        .map((entity) => entity.shortName || entity.name || "")
        .filter(Boolean)
        .join(" · ");
}
function personLabel(person) {
    if (!person)
        return "";
    return [person.name, person.surname].filter(Boolean).join(" ").trim();
}
/**
 * Umowa jest po terminie, gdy data zakończenia minęła, a status nie mówi, że to już zamknięta
 * sprawa. Zakończona i archiwalna umowa Z DEFINICJI ma datę w przeszłości — podświetlanie jej
 * byłoby ostrzeżeniem przed stanem normalnym, czyli szumem.
 */
function isEndDateOverdue(contract) {
    if (!contract.endDate)
        return false;
    const closed = [MainSetupReact_1.default.ContractStatuses.FINISHED, MainSetupReact_1.default.ContractStatuses.ARCHIVAL];
    if (closed.includes(contract.status))
        return false;
    return (0, LetterRowContent_1.isOverdue)(contract.endDate);
}
/** Wspólny ogon obu wariantów wiersza: typ, status i projekt. */
function LineTail({ contract }) {
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(CommonComponents_1.ContractTypeBadge, { type: contract._type, settlementMethod: contract.settlementMethod }),
        react_1.default.createElement(CommonComponents_1.ContractStatusBadge, { status: contract.status }),
        contract._project?.ourId && react_1.default.createElement("span", { style: S.proj }, contract._project.ourId)));
}
function OurContractBlock({ contract }) {
    const identifier = [contract.ourId, contract.alias].filter(Boolean).join(" | ");
    const employers = entitiesLabel(contract._employers);
    const coordinator = personLabel(contract._manager);
    const administrator = personLabel(contract._admin);
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("div", { style: S.line1 },
            react_1.default.createElement("span", { style: S.cid }, identifier),
            react_1.default.createElement(LineTail, { contract: contract })),
        react_1.default.createElement("div", { style: { ...S.hero, whiteSpace: "pre-line" } }, contract.name),
        (employers || coordinator || administrator) && (react_1.default.createElement("div", { style: S.under },
            employers && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement("b", { style: S.underStrong }, employers),
                " \u2014 Zamawiaj\u0105cy")),
            (coordinator || administrator) && (react_1.default.createElement(react_1.default.Fragment, null,
                employers && react_1.default.createElement("br", null),
                coordinator && (react_1.default.createElement(react_1.default.Fragment, null,
                    "koordynator: ",
                    react_1.default.createElement("b", { style: S.underStrong }, coordinator))),
                coordinator && administrator && " · ",
                administrator && (react_1.default.createElement(react_1.default.Fragment, null,
                    "administrator: ",
                    react_1.default.createElement("b", { style: S.underStrong }, administrator)))))))));
}
function OtherContractBlock({ contract }) {
    const contractors = entitiesLabel(contract._contractors);
    const employers = entitiesLabel(contract._employers);
    const engineers = entitiesLabel(contract._engineers);
    const relatedOurId = contract.ourIdRelated || contract._ourContract?.ourId || "";
    // Kotwica pamięciowa: alias i wykonawca. Gdy nie ma ani jednego, „hero" przejmuje nazwa
    // kontraktu — i wtedy pomijamy jej powtórzenie niżej, żeby nie stała w wierszu dwa razy
    // (ta sama reguła co w widoku zadań, TasksGlobal.makeOtherContractTitleHeader).
    const heroParts = [contract.alias, contractors].filter(Boolean);
    const hasAnchor = heroParts.length > 0;
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("div", { style: S.line1 },
            react_1.default.createElement("span", { style: S.hero }, hasAnchor
                ? heroParts.map((part, index) => (react_1.default.createElement(react_1.default.Fragment, { key: index },
                    index > 0 && react_1.default.createElement("span", { style: S.heroSep }, " \u00B7 "),
                    part)))
                : contract.name),
            react_1.default.createElement(LineTail, { contract: contract })),
        react_1.default.createElement("div", { style: S.cid },
            [contract._type?.name, contract.number].filter(Boolean).join(" · "),
            " ",
            react_1.default.createElement("span", { style: S.arrow }, "\u2794"),
            " ",
            relatedOurId ? (react_1.default.createElement("span", { style: S.related }, relatedOurId)) : (react_1.default.createElement("span", { style: S.noRelation }, "brak powi\u0105zania"))),
        hasAnchor && react_1.default.createElement("div", { style: { ...S.nameDemoted, whiteSpace: "pre-line" } }, contract.name),
        (employers || engineers) && (react_1.default.createElement("div", { style: S.under },
            employers && (react_1.default.createElement(react_1.default.Fragment, null,
                "Zamawiaj\u0105cy: ",
                react_1.default.createElement("b", { style: S.underStrong }, employers))),
            employers && engineers && " · ",
            engineers && (react_1.default.createElement(react_1.default.Fragment, null,
                "In\u017Cynier: ",
                react_1.default.createElement("b", { style: S.underStrong }, engineers)))))));
}
/**
 * Kolumna terminów. Rozpoczęcie i zakończenie stoją zawsze (są obowiązkowe), pozostałe
 * wyłącznie gdy wpisane. Kolejność chronologiczna: najpierw koniec robót, potem zgłaszanie
 * wad, na końcu okresy odpowiedzialności za wady.
 */
function ContractDates({ contract }) {
    const optional = [
        ["zgł. wad", contract.defectsNotificationEndDate],
        ["gwarancja", contract.guaranteeEndDate],
        ["rękojmia", contract.warrantyEndDate],
    ];
    const endOverdue = isEndDateOverdue(contract);
    return (react_1.default.createElement("div", { style: S.sideBox },
        react_1.default.createElement("div", { style: S.sideHead }, "Terminy"),
        react_1.default.createElement("div", { style: S.sideRow },
            react_1.default.createElement("span", { style: S.sideKey }, "rozpocz\u0119cie"),
            react_1.default.createElement("span", { style: S.sideValue }, (0, LetterRowContent_1.formatDate)(contract.startDate))),
        react_1.default.createElement("div", { style: S.sideRow },
            react_1.default.createElement("span", { style: S.sideKey }, "zako\u0144czenie"),
            react_1.default.createElement("span", { style: endOverdue ? S.sideValueOverdue : S.sideValue, title: endOverdue ? "Termin zakończenia minął, a umowa nie jest zamknięta" : undefined }, (0, LetterRowContent_1.formatDate)(contract.endDate))),
        optional
            .filter(([, value]) => !!value)
            .map(([label, value]) => (react_1.default.createElement("div", { key: label, style: S.sideRow },
            react_1.default.createElement("span", { style: S.sideKey }, label),
            react_1.default.createElement("span", { style: S.sideValue }, (0, LetterRowContent_1.formatDate)(value)))))));
}
/**
 * Dolny pasek: zakresy z lewej, plakietka integracji z prawej.
 *
 * Trzy stany `_isFidmanIntegrated` znaczą co innego i tylko jeden rysuje plakietkę:
 *   `undefined` — typ umowy nie podlega synchronizacji, plakietki nie ma wcale;
 *   `false`     — mógłby być zintegrowany, ale nie jest; też bez plakietki, bo to stan normalny
 *                 dla świeżej umowy, a nie wyróżnik (od szukania takich jest filtr w panelu);
 *   `true`      — plakietka.
 *
 * Plakietka to ten sam `FidmanSyncBadge`, co w karcie kontraktu (ContractMainHeader) — jeden
 * wygląd stanu integracji w całej aplikacji. Różni się ŹRÓDŁO odczytu i dlatego mapowanie jest
 * jawne: karta czyta stan kolejki wysyłkowej i przekazuje wszystkie pięć stanów, lista zna
 * wyłącznie trwały link `Contracts.FidmanContractId` i umie rozstrzygnąć tylko „jest w FIDmanie"
 * (`SENT`) albo „nie ma czego pokazać" (`NONE`). Lista NIE pokaże więc „do dopchnięcia" ani
 * „brakujące dane” — to widać w karcie kontraktu, gdzie jest też przycisk ponowienia.
 */
function ContractRowBottomBar({ contract }) {
    const names = contract._contractRangesNames ?? [];
    const isIntegrated = contract._isFidmanIntegrated === true;
    const isDocumentMissing = contract._isContractDocumentMissing === true;
    if (!names.length && !isIntegrated && !isDocumentMissing)
        return null;
    return (react_1.default.createElement("div", { style: S.bottom },
        react_1.default.createElement("div", { style: S.ranges }, names.map((name, index) => (react_1.default.createElement("span", { key: index, style: S.range }, name)))),
        react_1.default.createElement("span", { className: "d-flex flex-wrap align-items-center gap-2" },
            react_1.default.createElement(ContractDocumentBadge, { contract: contract }),
            isIntegrated && react_1.default.createElement(CommonComponents_1.FidmanSyncBadge, { status: "SENT" }))));
}
/**
 * Plakietka braku umowy na Dysku — wynik kontroli z zadania cyklicznego
 * (PS-nodeJS: contractDocuments/ContractDocumentsCheck).
 *
 * Rysowana WYŁĄCZNIE przy `true`. `false` znaczy „sprawdzono, umowa jest" — nie ma czego
 * pokazywać. `undefined` znaczy „NIE sprawdzano", bo umowa nie ma sprawy „umowa" (starsza
 * struktura folderów, ok. 166 z 785) albo kontrola tam jeszcze nie dotarła; oznaczanie takiej
 * umowy byłoby zarzutem bez pokrycia.
 *
 * Jest odnośnikiem otwierającym dokładnie ten folder, w którym pliku brakuje — komunikat
 * o braku ma być drogą do jego usunięcia, a nie samym zarzutem. Gdy backend nie oddał adresu
 * folderu, zostaje sama plakietka bez odnośnika.
 *
 * Kolor: `danger`, a nie `warning`. Obok stoi zielona plakietka FIDmana o odwrotnej wymowie
 * („wszystko w porządku") — dwie plakietki w jednej linii muszą się różnić na tyle, żeby nikt
 * nie odczytał ich jako dwóch stopni tego samego stanu.
 */
function ContractDocumentBadge({ contract }) {
    if (contract._isContractDocumentMissing !== true)
        return null;
    const checkedAt = contract._contractDocumentCheckedAt;
    const badge = (react_1.default.createElement(react_bootstrap_1.Badge, { bg: "danger", text: "light", title: checkedAt ? `Sprawdzono ${(0, LetterRowContent_1.formatDate)(checkedAt)}` : undefined }, "Uzupe\u0142nij umow\u0119 na dysku"));
    if (!contract._contractDocumentFolderUrl)
        return badge;
    return (react_1.default.createElement("a", { href: contract._contractDocumentFolderUrl, target: "_blank", rel: "noopener noreferrer", style: { textDecoration: "none" } }, badge));
}
function ContractRowContent({ contract }) {
    return (react_1.default.createElement("div", { style: { wordBreak: "break-word" } },
        react_1.default.createElement("div", { className: "d-flex flex-wrap", style: { gap: "18px", alignItems: "flex-start" } },
            react_1.default.createElement("div", { className: "d-flex flex-column", style: { gap: "8px", flex: "1 1 340px", minWidth: 0 } }, isOurContract(contract) ? (react_1.default.createElement(OurContractBlock, { contract: contract })) : (react_1.default.createElement(OtherContractBlock, { contract: contract }))),
            react_1.default.createElement("div", { style: { flex: "0 1 250px", minWidth: "200px" } },
                react_1.default.createElement(ContractDates, { contract: contract }))),
        react_1.default.createElement(ContractRowBottomBar, { contract: contract })));
}
